/**
 * BNPL rejected screen (ERS-5, step renumbered in ERS-78)
 *
 * Route: `/webAppOnboarding/insurance-bnpl-rejected`. Design is not final (ERS-12 owns the
 * reject-path product decision) — this is the smallest honest version: headline, explanation that
 * the course still starts without the BNPL rail, and a "Trotzdem starten" CTA to install. Copy is
 * v1's placeholder verbatim (2026-08-20 decision), centralized in `bnplCopy.ts` so swapping it
 * later is a one-file diff.
 *
 * ## Nothing navigates here (ERS-81)
 *
 * A decline is silent for the patient: it asks nothing of them and changes only how the finished
 * course is billed, so `useBnplPaymentScreen` sends a declined patient to install, the same place
 * an authorization lands. The screen, its route and its copy are kept reachable by URL because
 * ERS-12 may still decide the decline deserves its own screen — until then it never renders on its
 * own, so do not debug why it does not appear.
 *
 * Guards:
 * - Route: `OnboardingSignUpGateLayout` → `OnboardingAuthGuard` → `OnboardingCompletedGuard`.
 * - Auth: Firebase user required via `OnboardingAuthGuard`.
 * - Onboarding state: completed patients redirected to install by `OnboardingCompletedGuard`.
 * - Screen-local: `useSaveOnboardingStep('insurance_bnpl_rejected')`. Deliberately NO
 *   `useInsuranceExplainBillingGuard('bnpl')` — by the time this screen renders, the payment screen
 *   has already reverted the patient's billing type to the insurer's default, so that guard would
 *   immediately bounce the patient away from the very screen explaining the decline.
 *
 *   The same hazard exists one screen earlier and was missed until 2026-08-28: the guard on the
 *   *payment* screen sees the revert first, because the Firestore write reaches the store while
 *   `revertToInsurerDefault()` is still awaiting its server ack. It used to win the race and
 *   `replace` to the fallback insurer's process overview; this screen then loaded on top of it a
 *   round trip later, so the patient saw a flow they never chose flash past and PostHog recorded a
 *   `web_insurance_process_overview_shown` for it. `useBnplPaymentScreen` now suppresses its own
 *   guard for the duration of the decline (`exitHandledRef`). Removing that suppression brings the
 *   flash back — dropping the guard here is only half the fix.
 */

import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { space } from '../../../styles/onboardingUiTokens';
import { Notice } from '../components/elements/Notice';
import { OnboardingBody } from '../components/layout/OnboardingBody';
import { OnboardingFooter } from '../components/layout/OnboardingFooter';
import { OnboardingHeader } from '../components/layout/OnboardingHeader';
import { OnboardingLayout } from '../components/layout/OnboardingLayout';
import { Body } from '../components/typography/Body';
import { H1 } from '../components/typography/HeadingLevels';
import { BNPL_REJECTED_COPY } from '../insurance/bnplCopy';
import { useSaveOnboardingStep } from '../services/onboardingProgress';

export const ONBOARDING_SCREEN_META = {
  requiresFirebaseSession: true,
} as const;

const ContentColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space.lg};
`;

export function WebOnboardingInsuranceBnplRejectedScreen() {
  useSaveOnboardingStep('insurance_bnpl_rejected');
  const navigate = useNavigate();

  const handleForward = useCallback(() => {
    navigate('/webAppOnboarding/install');
  }, [navigate]);

  return (
    <OnboardingLayout
      currentStep={6}
      totalSteps={7}
      forwardLabel={BNPL_REJECTED_COPY.primaryCta}
      onForward={handleForward}
    >
      <OnboardingHeader />
      <OnboardingBody>
        <div style={{ paddingBottom: space.xl }}>
          <ContentColumn>
            <div style={{ display: 'flex', flexDirection: 'column', gap: space.sm }}>
              <H1>{BNPL_REJECTED_COPY.title}</H1>
              <Body variant="document" tone="muted">
                {BNPL_REJECTED_COPY.subtitle}
              </Body>
            </div>
            <Notice preset="warning" title="Warum passiert das?">
              {BNPL_REJECTED_COPY.disclaimer}
            </Notice>
          </ContentColumn>
        </div>
      </OnboardingBody>
      <OnboardingFooter />
    </OnboardingLayout>
  );
}
